import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Mail, Phone, MessageSquare, Package, Calendar, CheckCircle, Clock, XCircle } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Quotes = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedQuoteId, setSelectedQuoteId] = useState(null);
  const [products, setProducts] = useState({});

  useEffect(() => {
    if (authLoading) return;
    
    if (!user || !user.is_admin) {
      toast.error('Acesso negado. Apenas administradores.');
      navigate('/');
      return;
    }
    fetchQuotes();
    fetchProducts();
  }, [authLoading, user, navigate]);

  const fetchQuotes = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/admin/quotes`);
      setQuotes(response.data || []);
    } catch (error) {
      console.error('Failed to fetch quotes:', error);
      toast.error('Erro ao carregar orçamentos');
    } finally {
      setLoading(false);
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API_URL}/products`);
      const productsMap = {};
      response.data.forEach(p => {
        productsMap[p.id] = p;
      });
      setProducts(productsMap);
    } catch (error) {
      console.error('Failed to fetch products:', error);
    }
  };

  // Obter o orçamento selecionado diretamente do array de quotes
  const selectedQuote = quotes.find(q => q.id === selectedQuoteId) || null;

  const handleSelectQuote = useCallback((quoteId) => {
    setSelectedQuoteId(quoteId);
  }, []);

  const updateQuoteStatus = async (quoteId, status) => {
    try {
      await axios.put(`${API_URL}/admin/quotes/${quoteId}`, { status });
      toast.success('Status atualizado!');
      
      // Atualizar a lista localmente
      setQuotes(prevQuotes => 
        prevQuotes.map(q => 
          q.id === quoteId ? { ...q, status } : q
        )
      );
    } catch (error) {
      console.error('Failed to update quote:', error);
      toast.error('Erro ao atualizar status');
    }
  };

  const deleteQuote = async (quoteId) => {
    if (!window.confirm('Tem certeza que deseja excluir este orçamento? Esta ação não pode ser desfeita.')) {
      return;
    }
    
    try {
      await axios.delete(`${API_URL}/admin/quotes/${quoteId}`);
      toast.success('Orçamento excluído!');
      
      // Remover da lista localmente
      setQuotes(prevQuotes => prevQuotes.filter(q => q.id !== quoteId));
      
      // Limpar seleção se o orçamento excluído estava selecionado
      if (selectedQuoteId === quoteId) {
        setSelectedQuoteId(null);
      }
    } catch (error) {
      console.error('Failed to delete quote:', error);
      toast.error('Erro ao excluir orçamento');
    }
  };

  const generateDepositLink = async (quoteId) => {
    try {
      const res = await axios.post(`${API_URL}/payments/checkout/quote-deposit`, { quote_id: quoteId });
      const url = res.data.init_point;
      // Copiar para clipboard e atualizar quote localmente
      try {
        await navigator.clipboard.writeText(url);
        toast.success('Link de pagamento copiado! Envie ao cliente.');
      } catch {
        toast.success('Link de pagamento gerado.');
      }
      setQuotes(prev => prev.map(q => q.id === quoteId ? { ...q, deposit_checkout_url: url, deposit_order_id: res.data.order_id } : q));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao gerar link de pagamento');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatPrice = (price) => {
    if (!price && price !== 0) return 'R$ 0,00';
    return `R$ ${Number(price).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'text-yellow-600 bg-yellow-50';
      case 'contacted': return 'text-blue-600 bg-blue-50';
      case 'completed': return 'text-green-600 bg-green-50';
      case 'cancelled': return 'text-red-600 bg-red-50';
      default: return 'text-muted-foreground bg-muted';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'pending': return 'Pendente';
      case 'contacted': return 'Contatado';
      case 'completed': return 'Concluído';
      case 'cancelled': return 'Cancelado';
      default: return status || 'Pendente';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending': return <Clock className="w-4 h-4" />;
      case 'contacted': return <Mail className="w-4 h-4" />;
      case 'completed': return <CheckCircle className="w-4 h-4" />;
      case 'cancelled': return <XCircle className="w-4 h-4" />;
      default: return <Clock className="w-4 h-4" />;
    }
  };

  const calculateTotal = (items) => {
    if (!items || !Array.isArray(items)) return 0;
    return items.reduce((total, item) => {
      const price = item.custom_price || products[item.product_id]?.price || 0;
      return total + (price * (item.quantity || 1));
    }, 0);
  };

  if (authLoading || loading) {
    return (
      <div className="py-20 text-center">
        <p className="text-muted-foreground">Carregando orçamentos...</p>
      </div>
    );
  }

  return (
    <div data-testid="quotes-page" className="py-20 md:py-32">
      <div className="px-6 md:px-12 lg:px-24">
        {/* Header */}
        <div className="flex items-center justify-between mb-12">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/admin')}
              className="text-muted-foreground hover:text-foreground transition-colors"
            >
              <ArrowLeft className="w-5 h-5" strokeWidth={1.5} />
            </button>
            <h1 className="font-heading text-4xl md:text-5xl font-light tracking-tight">
              Orçamentos
            </h1>
          </div>
          <p className="text-sm text-muted-foreground">
            {quotes.length} orçamento{quotes.length !== 1 ? 's' : ''}
          </p>
        </div>

        {quotes.length === 0 ? (
          <div className="text-center py-20">
            <MessageSquare className="w-12 h-12 mx-auto text-muted-foreground mb-4" strokeWidth={1} />
            <p className="text-muted-foreground">Nenhum orçamento recebido ainda.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Lista de Orçamentos */}
            <div className="lg:col-span-1 space-y-4">
              {quotes.map((quote, index) => (
                <div
                  key={`quote-${quote.id || index}`}
                  onClick={() => handleSelectQuote(quote.id)}
                  data-quote-id={quote.id}
                  className={`border p-4 cursor-pointer transition-all duration-300 ${
                    selectedQuoteId === quote.id 
                      ? 'border-primary bg-muted/30' 
                      : 'border-border hover:border-primary/50'
                  }`}
                >
                  <div className="flex justify-between items-start mb-2">
                    <h3 className="font-medium">{quote.name || 'Sem nome'}</h3>
                    <span className={`text-xs px-2 py-1 rounded flex items-center gap-1 ${getStatusColor(quote.status)}`}>
                      {getStatusIcon(quote.status)}
                      {getStatusLabel(quote.status)}
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground mb-2">{quote.email || 'Sem email'}</p>
                  <div className="flex justify-between items-center text-xs text-muted-foreground">
                    <span className="flex items-center gap-1">
                      <Package className="w-3 h-3" />
                      {quote.items?.length || 0} {(quote.items?.length || 0) === 1 ? 'item' : 'itens'}
                    </span>
                    <span className="flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      {formatDate(quote.created_at)}
                    </span>
                  </div>
                </div>
              ))}
            </div>

            {/* Detalhes do Orçamento */}
            <div className="lg:col-span-2">
              {selectedQuote ? (
                <div className="border border-border p-6" key={`detail-${selectedQuote.id}`}>
                  <div className="flex justify-between items-start mb-6">
                    <div>
                      <h2 className="font-heading text-2xl font-light mb-2">{selectedQuote.name || 'Sem nome'}</h2>
                      <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                        {selectedQuote.email && (
                          <a href={`mailto:${selectedQuote.email}`} className="flex items-center gap-1 hover:text-primary">
                            <Mail className="w-4 h-4" />
                            {selectedQuote.email}
                          </a>
                        )}
                        {selectedQuote.phone && (
                          <a href={`tel:${selectedQuote.phone}`} className="flex items-center gap-1 hover:text-primary">
                            <Phone className="w-4 h-4" />
                            {selectedQuote.phone}
                          </a>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <select
                        value={selectedQuote.status || 'pending'}
                        onChange={(e) => updateQuoteStatus(selectedQuote.id, e.target.value)}
                        data-testid="quote-status-select"
                        className={`text-xs px-3 py-2 border border-border bg-white focus:outline-none focus:border-primary ${getStatusColor(selectedQuote.status)}`}
                      >
                        <option value="pending">Pendente</option>
                        <option value="contacted">Contatado</option>
                        <option value="approved">Aprovado</option>
                        <option value="completed">Concluído</option>
                        <option value="cancelled">Cancelado</option>
                      </select>
                      <button
                        onClick={() => deleteQuote(selectedQuote.id)}
                        className="text-xs px-3 py-2 border border-red-300 text-red-600 hover:bg-red-50 transition-colors"
                        title="Excluir orçamento"
                      >
                        Excluir
                      </button>
                    </div>
                  </div>

                  {/* Link de pagamento de sinal (só orçamentos aprovados) */}
                  {selectedQuote.status === 'approved' && (
                    <div className="mb-6 border border-neutral-900 p-5" data-testid="deposit-section">
                      <p className="font-accent text-[11px] tracking-[0.3em] uppercase text-neutral-500 mb-3">
                        Cobrança de sinal (50%)
                      </p>
                      {selectedQuote.deposit_status === 'paid' ? (
                        <p className="text-sm text-emerald-700 font-medium">✓ Sinal pago em {selectedQuote.deposit_paid_at ? new Date(selectedQuote.deposit_paid_at).toLocaleString('pt-BR') : ''}</p>
                      ) : selectedQuote.deposit_checkout_url ? (
                        <div className="space-y-3">
                          <p className="text-xs text-neutral-600">Link já gerado. Envie ao cliente por WhatsApp / e-mail:</p>
                          <div className="flex gap-2">
                            <input
                              readOnly
                              value={selectedQuote.deposit_checkout_url}
                              className="flex-1 border border-neutral-300 px-3 py-2 text-xs bg-neutral-50"
                              onFocus={(e) => e.target.select()}
                              data-testid="deposit-link-input"
                            />
                            <button
                              onClick={() => {
                                navigator.clipboard.writeText(selectedQuote.deposit_checkout_url);
                                toast.success('Copiado!');
                              }}
                              className="text-xs px-4 py-2 bg-neutral-900 text-white uppercase tracking-widest"
                              data-testid="copy-deposit-link"
                            >
                              Copiar
                            </button>
                            <button
                              onClick={() => generateDepositLink(selectedQuote.id)}
                              className="text-xs px-4 py-2 border border-neutral-300 uppercase tracking-widest hover:bg-neutral-100"
                            >
                              Gerar novo
                            </button>
                          </div>
                        </div>
                      ) : (
                        <button
                          onClick={() => generateDepositLink(selectedQuote.id)}
                          data-testid="generate-deposit-link"
                          className="text-xs px-6 py-3 bg-neutral-900 text-white uppercase tracking-widest hover:bg-black"
                        >
                          Gerar link de pagamento 50%
                        </button>
                      )}
                    </div>
                  )}

                  {selectedQuote.message && (
                    <div className="mb-6 p-4 bg-muted/30 border-l-2 border-primary">
                      <p className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Mensagem</p>
                      <p className="text-sm">{selectedQuote.message}</p>
                    </div>
                  )}

                  <div className="mb-6">
                    <p className="text-xs uppercase tracking-widest text-muted-foreground mb-4">Itens do Orçamento</p>
                    
                    {selectedQuote.items && selectedQuote.items.length > 0 ? (
                      <div className="space-y-4">
                        {selectedQuote.items.map((item, itemIndex) => {
                          const product = products[item.product_id];
                          return (
                            <div key={`item-${selectedQuote.id}-${itemIndex}`} className="flex gap-4 border-b border-border pb-4">
                              {product?.images?.[0] && (
                                <img
                                  src={product.images[0]}
                                  alt={product?.name || 'Produto'}
                                  className="w-20 h-20 object-cover"
                                />
                              )}
                              <div className="flex-1">
                                <h4 className="font-medium">{product?.name || 'Produto não encontrado'}</h4>
                                <p className="text-sm text-muted-foreground">
                                  Quantidade: {item.quantity || 1}
                                </p>
                                
                                {/* Customização */}
                                {item.custom_config && Object.keys(item.custom_config).length > 0 && (
                                  <div className="mt-2 text-xs text-muted-foreground space-y-1">
                                    {item.custom_config.fabric && (
                                      <p>Tecido: <span className="text-foreground">{item.custom_config.fabric.name}</span></p>
                                    )}
                                    {item.custom_config.size && (
                                      <p>Tamanho: <span className="text-foreground">{item.custom_config.size.name}</span></p>
                                    )}
                                    {item.custom_config.material && (
                                      <p>Material: <span className="text-foreground">{item.custom_config.material.name}</span></p>
                                    )}
                                  </div>
                                )}
                              </div>
                              <div className="text-right">
                                <p className="font-medium">
                                  {formatPrice(item.custom_price || product?.price)}
                                </p>
                                {(item.quantity || 1) > 1 && (
                                  <p className="text-xs text-muted-foreground">
                                    x{item.quantity} = {formatPrice((item.custom_price || product?.price || 0) * (item.quantity || 1))}
                                  </p>
                                )}
                              </div>
                            </div>
                          );
                        })}
                        
                        {/* Total */}
                        <div className="flex justify-between items-center pt-4 border-t border-border">
                          <p className="font-accent text-sm uppercase tracking-widest">Total Estimado</p>
                          <p className="font-heading text-2xl font-light">
                            {formatPrice(calculateTotal(selectedQuote.items))}
                          </p>
                        </div>
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground">Nenhum item no orçamento.</p>
                    )}
                  </div>

                  <div className="text-xs text-muted-foreground border-t border-border pt-4">
                    <p>Recebido em: {formatDate(selectedQuote.created_at)}</p>
                    <p>ID: {selectedQuote.id}</p>
                  </div>
                </div>
              ) : (
                <div className="border border-dashed border-border p-12 text-center">
                  <MessageSquare className="w-12 h-12 mx-auto text-muted-foreground mb-4" strokeWidth={1} />
                  <p className="text-muted-foreground">Selecione um orçamento para ver os detalhes</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Quotes;
